import { store } from "../app/store";
import {
  type IHotelRate,
  type IHotelRoomOccupancyAvailabilityPrice,
  type IHotelRoomOccupancyAvailabilityPricing,
} from "../features/hotel/hotelInterface";

/**
 * Find rate from Rates
 * @param Rates
 * @param Id
 * @returns IHotelRate | undefined
 */
export const findRate = (Id: IHotelRate["Id"]): IHotelRate | undefined => {
  const { hotel } = store.getState();
  return hotel.Rates.find((r) => r.Id === Id);
};

export const findRateByGroup = (RateGroupId: IHotelRate["RateGroupId"]): IHotelRate | undefined => {
  const { hotel } = store.getState();
  return hotel.Rates.find((r) => r.RateGroupId === RateGroupId);
};

/**
 * The public rate of the same rate group as a private (voucher) rate, or the rate itself
 */
export const findPublicRate = (Id: IHotelRate["Id"]): IHotelRate | undefined => {
  const { hotel } = store.getState();
  const rate = hotel.Rates.find((r) => r.Id === Id);
  if (!rate?.IsPrivate) return rate;
  return (
    [...hotel.Rates]
      .filter((r) => !r.IsPrivate && r.RateGroupId === rate.RateGroupId)
      .sort((a, b) => a.Ordering - b.Ordering)[0] ?? rate
  );
};

type RatePrice = {
  RateId: string;
  Price: IHotelRoomOccupancyAvailabilityPrice;
  MaxPrice: IHotelRoomOccupancyAvailabilityPrice;
  ReferencePrice?: IHotelRoomOccupancyAvailabilityPrice;
};

/**
 * Show each public rate with the price of the cheaper voucher rate of its rate group,
 * and the public price as reference. The voucher rate itself is hidden.
 */
export const combineVoucherRates = <T extends RatePrice>(
  items: T[],
  currencyCode: string,
): (T & { VoucherRateId?: string })[] => {
  const { hotel } = store.getState();
  const rateById = new Map(hotel.Rates.map((r) => [r.Id, r]));
  const total = (p: RatePrice) => p.Price.Total[currencyCode] ?? 0;

  return items.flatMap((item) => {
    const rate = rateById.get(item.RateId);
    if (!rate) return [item];

    const sameGroup = items.filter(
      (i) => i !== item && rateById.get(i.RateId)?.RateGroupId === rate.RateGroupId,
    );

    if (rate.IsPrivate) {
      return sameGroup.some((i) => !rateById.get(i.RateId)?.IsPrivate) ? [] : [item];
    }

    const voucher = sameGroup
      .filter((i) => rateById.get(i.RateId)?.IsPrivate && total(i) < total(item))
      .reduce<T | undefined>((best, i) => (!best || total(i) < total(best) ? i : best), undefined);
    if (!voucher) return [item];

    return [
      {
        ...item,
        Price: voucher.Price,
        MaxPrice: voucher.MaxPrice,
        ReferencePrice: item.Price,
        VoucherRateId: voucher.RateId,
      },
    ];
  });
};

export const findHighestRatePricing = (
  pricing: IHotelRoomOccupancyAvailabilityPricing[],
  currencyCode: string,
): IHotelRoomOccupancyAvailabilityPricing | null => {
  if (!pricing.length) return null;

  return pricing.reduce((highest, current) => {
    const currentPrice = current.Price?.AveragePerNight?.[currencyCode] ?? 0;
    const highestPrice = highest.Price?.AveragePerNight?.[currencyCode] ?? 0;
    return currentPrice > highestPrice ? current : highest;
  });
};

export const isReferenceRate = (
  rateId: string,
  referenceRateId: string | null,
): boolean => {
  return referenceRateId !== null && rateId === referenceRateId;
};

export const getReferencePriceForRate = (
  pricing: IHotelRoomOccupancyAvailabilityPricing[],
  rateId: string,
  currencyCode: string,
): IHotelRoomOccupancyAvailabilityPrice | null => {
  const reference = findHighestRatePricing(pricing, currencyCode);
  if (!reference || reference.RateId === rateId) return null;

  const current = pricing.find((p) => p.RateId === rateId);
  if (!current) return null;

  const refPrice = reference.Price?.AveragePerNight?.[currencyCode] ?? 0;
  const currentPrice = current.Price?.AveragePerNight?.[currencyCode] ?? 0;
  if (refPrice <= currentPrice) return null;

  return reference.Price;
};
